/**
 * The client and matter hub, base (legal) dialect. Strings that already exist
 * in `project:` (the matters list heading, add buttons, search labels and
 * empty states) are reused from there, not repeated. Counts and amounts are
 * formatted by the caller and passed in as `{{formattedCount}}` and
 * `{{amount}}`; `count` selects the plural form.
 */
export const hub = {
    breadcrumbs: {
        clients: 'Clients',
        clientFallback: 'Client',
        matterFallback: 'Matter',
    },
    loading: {
        client: 'Loading client...',
    },
    loadError: {
        client: "Couldn't load this client",
    },
    status: {
        deleted: 'Deleted',
        inactive: 'Inactive',
    },
    deletedBanner: {
        client: 'This client was deleted. Past invoices and financial records are kept.',
        matter: 'This matter was deleted. Its time, invoices and other records are kept.',
    },
    figures: {
        wip: 'Work in progress',
        outstanding: 'Outstanding',
        paidThisYear: 'Paid this year',
        trustTopUp: 'Top up needed',
        trustHeld: 'Funds held in trust',
        loadError: "Couldn't load these figures",
    },
    matterStatement: {
        breadcrumb: 'Matter statement',
        empty: 'Nothing on this matter in this period.',
        loadError: "Couldn't load this matter's statement.",
        unpaidInvoicesLoadError: "Couldn't load this matter's unpaid invoices.",
    },
    clientStatementMatter: {
        matterDueByClient: 'Due by client for this matter',
        matterDueToClient: 'Due to client for this matter',
        noUnpaidInvoices: 'No unpaid invoices on this matter',
    },
    header: {
        clientStatement: 'Client statement',
        matterStatement: 'Matter statement',
        matterActions: 'Matter actions',
        editDetails: 'Edit details',
        markInactive: 'Mark as inactive',
    },
    timeline: {
        title: 'Timeline',
        filterLabel: 'Filter the timeline',
        loading: 'Loading timeline...',
        loadError: "Couldn't load the timeline",
        loadMoreError: "Couldn't load more",
        openError: "Couldn't open the activity",
        emptyTitle: 'Nothing on the timeline yet',
        emptyDescription: 'Activities, invoices, payments, expenses and emails for {{name}} appear here as they happen.',
        emptyFilter: 'Nothing to show here.',
        showMore: 'Show more',
        chip: {
            all: 'All',
            activity: 'Activities',
            invoice: 'Invoices',
            payment: 'Payments',
            expense: 'Expenses',
            email: 'Emails',
        },
        type: {
            activity: 'Activity',
            invoice: 'Invoice',
            payment: 'Payment',
            expense: 'Expense',
            email: 'Email',
        },
        row: {
            invoiceIssued: 'Invoice {{number}} issued',
            invoiceIssuedNoNumber: 'Invoice issued',
            invoiceVoided: 'Invoice {{number}}',
            invoiceVoidedNoNumber: 'Invoice',
            payment: 'Payment received',
            expense: '{{supplier}}',
            expenseWithReference: '{{supplier}} – {{reference}}',
            emailNoSubject: '(No subject)',
        },
        badge: {
            overdue: 'Overdue',
            overdueNotSent: 'Overdue – not sent',
            voided: 'Voided',
            notBilled: 'Not billed',
            noMatter: 'No matter',
        },
        action: {
            logActivity: 'Log activity',
            createInvoice: 'Create invoice',
            createExpense: 'Create expense',
            viewActivities: 'View all activities',
            viewInvoices: 'Open invoices',
            viewExpenses: 'Open expenses',
            viewEmails: 'View all emails',
            viewStatement: 'View statement',
        },
        emailFilter: {
            label: 'Showing one email',
            clear: 'Show all emails',
        },
    },
    contact: {
        trigger: 'Contact info',
        title: 'Contact details',
        email: 'Email',
        emailCopied: 'Email copied',
        copy: 'Copy',
        phone: 'Phone',
        address: 'Address',
        none: 'No contact details.',
        edit: 'Edit client details',
    },
    matters: {
        showAll: 'Show all {{formattedCount}}',
        showFewer: 'Show fewer',
        showMore: 'Show more',
        statusFilterLabel: 'Filter matters by status',
        statusActive: 'Active',
        statusInactive: 'Inactive',
        statusAll: 'All',
        columnReference: 'Ref',
        columnWip: 'WIP',
        columnOutstanding: 'Outstanding',
        columnLastActivity: 'Last activity',
        sortAscending: 'Sort by {{column}} ascending',
        sortDescending: 'Sort by {{column}} descending',
        noActivity: 'No activity',
        loadError: "Couldn't load matters",
    },
    switcher: {
        trigger: 'Switch matter: {{name}}',
        allMatters: 'All matters',
        active: 'Active',
        inactiveGroup: 'Inactive ({{formattedCount}})',
        inactiveGroupAtLeast: 'Inactive ({{formattedCount}}+)',
        optionDetail: '{{reference}} · WIP {{amount}}',
    },
};
